import SpeechBubble from '@design-system/components/molecules/SpeechBubble'
import { Icon } from '@design-system/index'
import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import clsx from 'clsx'
import { addDays, isAfter } from 'date-fns'
import { observer } from 'mobx-react'
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation, useRouteMatch } from 'react-router'

import { CustomEventName, getContractUrl } from '@/constant'
import type { Banner } from '@/dto'
import commonRepo from '@/repository/common.repo'
import { RouteName } from '@/router'
import BannerService from '@/service/banner.service'
import { localStorageService } from '@/service/storage.service'
import { openLink } from '@/utils/common'
import { mixpanelClient } from '@/utils/trackings/mixpanel'

import TooltipBanner from './TooltipBanner'

const Profile = () => {
  const isMyInfo = useRouteMatch(RouteName.myInfo)
  const { pathname } = useLocation()
  const [isBannerClosed, setIsBannerClosed] = useState(false)
  const isBannerOn = useMemo(() => {
    // 닫은 적 없거나, 닫은 지 24시간 지났으면 노출 가능
    const closedAt = localStorageService.getItem('CONTRACT_TOOLTIP_CLOSED_AT')
    const canShowByTime = !closedAt || isAfter(new Date(), addDays(new Date(closedAt), 1))
    if (commonRepo.isSchoolflat) {
      return !!(
        commonRepo.academy?.customerType !== 'B2B-제휴-김과외' &&
        commonRepo.academy?.status === 'FREE_INPROGRESS' &&
        commonRepo.user?.isAdmin &&
        !isMyInfo &&
        !isBannerClosed &&
        canShowByTime
      )
    }

    return !!(
      commonRepo.academy?.customerType !== 'B2B-제휴-김과외' &&
      commonRepo.academy?.status === 'FREE_INPROGRESS' &&
      !commonRepo.academy.isFreeInProgressSales &&
      commonRepo.user?.isAdmin &&
      !isMyInfo &&
      !isBannerClosed &&
      canShowByTime
    )
  }, [isMyInfo, pathname, commonRepo.academy, commonRepo.user, isBannerClosed])

  useEffect(() => {
    const onExpired = () => {
      setIsBannerClosed(false)
    }

    window.addEventListener(CustomEventName.Banner.onExpired, onExpired)

    return () => {
      window.removeEventListener(CustomEventName.Banner.onExpired, onExpired)
    }
  }, [])

  if (commonRepo.isSchoolflat) {
    return (
      <SchoolflatProfile
        banner={BannerService.banner}
        isBannerOn={isBannerOn}
        setIsBannerClosed={setIsBannerClosed}
      />
    )
  }

  return (
    <MathflatProfile
      isBannerOn={isBannerOn}
      banner={BannerService.banner}
      setIsBannerClosed={setIsBannerClosed}
    />
  )
}

const MathflatProfile = ({
  banner,
  isBannerOn,
  setIsBannerClosed,
}: {
  banner?: Banner
  isBannerOn: boolean
  setIsBannerClosed: Dispatch<SetStateAction<boolean>>
}) => {
  const onCloseButtonClick = () => {
    setIsBannerClosed(true)
    localStorageService.setItem('CONTRACT_TOOLTIP_CLOSED_AT')(new Date().getTime())
    mixpanelClient.track('T99-04_정식가입하러가기툴팁_닫기')
  }

  const onTooltipBannerClick = (banner: Banner) => {
    if (banner) {
      let url = banner.redirectUrl

      if (url && !url.startsWith('http')) {
        url = 'http://' + url
      }

      if (url && url.includes(`contract.${domainProvider.TARGET_DOMAIN}`) && commonRepo.user) {
        url = getContractUrl(commonRepo.user, url)
      }

      if (url) {
        openLink(url)
      }

      mixpanelClient.track('T99-04_정식가입하러가기툴팁_신청하기클릭')
    }
  }

  if (!banner) return <_Profile />

  return (
    <SpeechBubble
      visible={isBannerOn}
      closeButtonOpt={{ hasCloseButton: true, isCloseButtonVisible: false, style: { top: 12 } }}
      showArrow={false}
      Overlay={<TooltipBanner banner={banner} onClick={onTooltipBannerClick} />}
      overlayBackgroundColor="transparent"
      overlayBorderColor="transparent"
      placement="bottom-right"
      align={{ offset: [-23, -2] }}
      overlayStyle={{ width: 280 }}
      onCloseButtonClick={onCloseButtonClick}
    >
      <_Profile />
    </SpeechBubble>
  )
}

const SchoolflatProfile = ({
  banner,
  isBannerOn,
  setIsBannerClosed,
}: {
  banner?: Banner
  isBannerOn: boolean
  setIsBannerClosed: Dispatch<SetStateAction<boolean>>
}) => {
  if (banner && isBannerOn) {
    return (
      <MathflatProfile
        banner={banner}
        isBannerOn={isBannerOn}
        setIsBannerClosed={setIsBannerClosed}
      />
    )
  }

  return <_Profile />
}

const _Profile = observer(() => {
  const history = useHistory()
  const { pathname } = useLocation()

  const ProfileIcon = (() => {
    if (commonRepo.permission.hasPremiumAuth) {
      return (
        <div className="profile__star-wrapper">
          <Icon className="profile__star-icon" name="star_fill" size={10} color="white" />
        </div>
      )
    }
    return (
      <Icon
        className="profile__profile-icon"
        name="profile"
        size={20}
        color="var(--Colors-Gray-400)"
      />
    )
  })()

  return (
    <button
      className={clsx('header__profile', pathname.includes(RouteName.myInfo) && 'active')}
      onClick={() => {
        if (RouteName.myUserInfo === pathname) return
        history.push(RouteName.myUserInfo)
        mixpanelClient.track('GNB_마이페이지버튼_Click')
      }}
    >
      {ProfileIcon}
      <span className="profile__teacher-nickname">{commonRepo.user?.name}</span>
    </button>
  )
})
export default observer(Profile)
